import { useEffect, useRef, useState } from "react"
import DatalakeService from "src/services/datalake-service"
import EmissaoService from "src/services/emissao-service"

const initialState = { data: null, loading: false, error: null }

const toSummaryState = (data) => ({
    data: data ?? null,
    loading: false,
    error: null,
})

const getMostRecentFact = (facts) => {
    if (!Array.isArray(facts) || facts.length === 0) return null

    return [...facts].sort(
        (first, second) =>
            new Date(second?.DataReferencia ?? 0) -
            new Date(first?.DataReferencia ?? 0)
    )[0]
}

export const useCompanySummaryData = ({ company, refreshKey = 0 }) => {
    const datalakeServiceRef = useRef(new DatalakeService())
    const emissaoServiceRef = useRef(new EmissaoService())
    const [state, setState] = useState({
        rating: initialState,
        event: initialState,
        fact: initialState,
        probabilityDefault: initialState,
    })

    useEffect(() => {
        const cpfCnpj = company?.CPFCNPJ
        const codigoCvm = company?.CodigoCVM
        const raizCnpj = company?.RaizCNPJ
        let active = true

        if (!cpfCnpj) {
            setState({
                rating: initialState,
                event: initialState,
                fact: initialState,
                probabilityDefault: initialState,
            })
            return undefined
        }

        setState({
            rating: { ...initialState, loading: true },
            event: { ...initialState, loading: true },
            fact: codigoCvm ? { ...initialState, loading: true } : initialState,
            probabilityDefault: raizCnpj
                ? { ...initialState, loading: true }
                : initialState,
        })

        const load = async () => {
            const [rating, event, fact, probabilityDefault] = await Promise.allSettled([
                emissaoServiceRef.current.getUltimoRatingDevedor(cpfCnpj),
                emissaoServiceRef.current.getUltimoEventoCreditoDevedor(cpfCnpj),
                codigoCvm
                    ? datalakeServiceRef.current.loadFatoRelevante(codigoCvm)
                    : Promise.resolve(null),
                raizCnpj
                    ? datalakeServiceRef.current.loadPD(raizCnpj)
                    : Promise.resolve(null),
            ])

            if (!active) return

            setState({
                rating:
                    rating.status === "fulfilled"
                        ? toSummaryState(rating.value?.body?.data)
                        : { ...initialState, error: "Não foi possível carregar o rating." },
                event:
                    event.status === "fulfilled"
                        ? toSummaryState(event.value?.body?.data)
                        : { ...initialState, error: "Não foi possível carregar o evento de crédito." },
                fact: !codigoCvm
                    ? initialState
                    : fact.status === "fulfilled"
                        ? toSummaryState(getMostRecentFact(fact.value?.body?.results))
                        : { ...initialState, error: "Não foi possível carregar o fato relevante." },
                probabilityDefault: !raizCnpj
                    ? initialState
                    : probabilityDefault.status === "fulfilled"
                        ? toSummaryState(probabilityDefault.value?.body?.results?.[0])
                        : { ...initialState, error: "Não foi possível carregar a probabilidade de default." },
            })
        }

        void load()

        return () => {
            active = false
        }
    }, [company?.CPFCNPJ, company?.CodigoCVM, company?.RaizCNPJ, refreshKey])

    return state
}
